import React from 'react';
import { FilterModel, ReportResponseModel } from 'core/models';
import { PendoClassNames } from 'components/feature/Report/ReportSidebar/common/pendoClassNames';
import { OperatorTypes } from 'core/constants/report';
import { isValidObject } from 'components/feature/Report/ReportSidebar/common/helpers';
import { DefaultValueForNumberFilterOperators, NumberFilterOperatorTypes } from 'core/constants/filter';
import SingleSelect from '../Dropdown/single-select-dropdown.component';
import InputField from '../InputField/input-field.component';
import StyledNumberFilterContainer from './number-filter.styled';
import useLocalize from '../Localization/useLocalize.hook';

const NumberFilterOptions: Array<{ label: string, value: Exclude<OperatorTypes, OperatorTypes.IN> }> = [
  {
    label: `${useLocalize('fltr.operatorIs')}`,
    value: OperatorTypes.EQ,
  },
  {
    label: `${useLocalize('fltr.operatorIsNot')}`,
    value: OperatorTypes.NOT_EQ,
  },
  {
    label: `${useLocalize('fltr.operatorLessThanOrEqualTo')}`,
    value: OperatorTypes.LTE,
  },
  {
    label: `${useLocalize('fltr.operatorGreaterThanOrEqualTo')}`,
    value: OperatorTypes.GTE,
  },
  {
    label: `${useLocalize('fltr.operatorBetween')}`,
    value: OperatorTypes.BETWEEN,
  },
  {
    label: `${useLocalize('fltr.operatorNoValue')}`,
    value: OperatorTypes.IS_NULL,
  },
  {
    label: `${useLocalize('fltr.operatorIsDefined')}`,
    value: OperatorTypes.IS_NOT_NULL,
  },
];

export interface INumberFilterComponentProps {
  configData: FilterModel.IConfig;
  value: any;
  removedBorderClass: string;
  addBorderClass: string;
  filterConfig: ReportResponseModel.IFilterConfig;
  onChange: (key: string, value: any) => void;
  selectedOperator?: string;
}

const NumberFilterComponent = ({
  configData,
  value,
  removedBorderClass,
  addBorderClass,
  filterConfig,
  selectedOperator,
  onChange,
}: INumberFilterComponentProps) => {
  const onNumberChange = (key: string, numberValue: any) => {
    // Handle number input change - update the filter with the new number value
    const updatedValue = {
      ...(isValidObject(value) ? value : {}),
      Value: numberValue,
    };
    onChange(key, updatedValue);
  };

  const onOperatorChange = ((key: string, operatorValue: Exclude<OperatorTypes, OperatorTypes.IN>) => {
    // Handle operator change - update the filter with the new operator
    let updatedValue = {};
    if (operatorValue === OperatorTypes.BETWEEN) {
      updatedValue = {
        ...updatedValue,
        Operator: operatorValue,
        Type: NumberFilterOperatorTypes.Absolute,
        Value: DefaultValueForNumberFilterOperators[OperatorTypes.BETWEEN],
      };
    } else {
      updatedValue = {
        ...updatedValue,
        Operator: operatorValue,
        Value: DefaultValueForNumberFilterOperators[operatorValue],
      };
    }
    onChange(key, updatedValue);
  });

  const isBetweenOperator = selectedOperator === OperatorTypes.BETWEEN;

  const renderNumberInputs = () => {
    if (isBetweenOperator) {
      const rangeValue = value?.Value || {};
      return (
        <div className="se-between-inputs">
          <InputField
            uniqKey={`${filterConfig.ID}_from`}
            type="number"
            inputValue={rangeValue.From ?? ''}
            onInputChange={(e) => {
              const updatedRange = {
                ...rangeValue,
                From: Number(e.target.value),
              };
              onNumberChange(filterConfig.ID, updatedRange);
            }}
            label=""
            placeholder={`${useLocalize('cmn.from')}`}
            wrapperClassName="range-input-wrapper"
          />
          <InputField
            uniqKey={`${filterConfig.ID}_to`}
            type="number"
            inputValue={rangeValue.To ?? ''}
            onInputChange={(e) => {
              const updatedRange = {
                ...rangeValue,
                To: Number(e.target.value),
              };
              onNumberChange(filterConfig.ID, updatedRange);
            }}
            label=""
            placeholder={`${useLocalize('cmn.to')}`}
            wrapperClassName="range-input-wrapper"
          />
        </div>
      );
    }

    // For single value operations (IS_NULL and IS_NOT_NULL doesn't need input)
    if (selectedOperator === OperatorTypes.IS_NULL || selectedOperator === OperatorTypes.IS_NOT_NULL) {
      return null;
    }

    return (
      <InputField
        uniqKey={filterConfig.ID}
        type="number"
        inputValue={value?.Value ?? ''}
        onInputChange={(e) => {
          onNumberChange(filterConfig.ID, Number(e.target.value));
        }}
        label=""
        placeholder={`${useLocalize('cmn.enterValue')}`}
        wrapperClassName="number-input-wrapper"
      />
    );
  };

  return (
    <StyledNumberFilterContainer isBetweenOperator={isBetweenOperator}>
      <SingleSelect
        config={{
          ...configData,
          IsMulti: false,
          Meta: {
            Options: NumberFilterOptions,
            DropdownIndicator: true,
            AppliedOnClose: false,
            CloseOnSelect: true,
          },
        }}
        value={selectedOperator}
        onChange={(val, fullVal) => {
          onOperatorChange(filterConfig.ID, fullVal);
        }}
        removeBorderClass={removedBorderClass}
        addBorderClass={addBorderClass}
        pendoClassName={PendoClassNames.DropDownFilters}
      />
      {renderNumberInputs()}
    </StyledNumberFilterContainer>
  );
};

export default NumberFilterComponent;
